import React, { useState } from 'react';
import { trpc } from "@/trpc/client";
import { useAuthStore } from "@/store/authStore";
import { toast } from "sonner";
import { format } from 'date-fns';

import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ShieldAlert, CheckCircle, Clock, Loader2, ChevronLeft, ChevronRight, Search, XCircle, Building2, Truck } from "lucide-react";
import { Input } from '@/components/ui/input';
import { useDebounce } from '@/hooks/useDebounce';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { VerificationStatus } from '@logistics-platform/types';

// --- Verification Rows ---

const VehicleVerificationRow = ({ vehicle, refetch }: { vehicle: any, refetch: () => void }) => {
    const { mutate, isLoading } = trpc.admin.verifyVehicle.useMutation({
        onSuccess: () => {
            toast.success(`Vehicle ${vehicle.licensePlate} has been verified.`);
            refetch();
        },
        onError: (error) => toast.error(error.message)
    });

    return (
        <TableRow>
            <TableCell className="font-medium">{vehicle.transporter?.profile?.fullName || 'N/A'}</TableCell>
            <TableCell>{vehicle.make} {vehicle.model}</TableCell>
            <TableCell className="font-mono">{vehicle.licensePlate}</TableCell>
            <TableCell>{format(new Date(vehicle.createdAt), 'PPP')}</TableCell>
            <TableCell className="text-right">
                <Button
                    size="sm"
                    variant="default"
                    disabled={isLoading}
                    onClick={() => mutate({ vehicleId: vehicle.id })}
                    className="bg-green-600 hover:bg-green-700 text-white"
                >
                    {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                    Approve
                </Button>
            </TableCell>
        </TableRow>
    )
}

const CompanyVerificationRow = ({ user, refetch }: { user: any, refetch: () => void }) => {
    const { mutate: approve, isLoading: isApproving } = trpc.admin.approveUser.useMutation({
        onSuccess: () => {
            toast.success(`User ${user.email} has been approved.`);
            refetch();
        },
        onError: (error) => toast.error(error.message)
    });

    const { mutate: reject, isLoading: isRejecting } = trpc.admin.rejectUser.useMutation({
        onSuccess: () => {
            toast.success(`User ${user.email} has been rejected.`);
            refetch();
        },
        onError: (error) => toast.error(error.message)
    });

    return (
        <TableRow>
            <TableCell className="font-medium">{user.profile?.organizationName || 'N/A'}</TableCell>
            <TableCell>{user.profile?.fullName}</TableCell>
            <TableCell>{user.email}</TableCell>
            <TableCell className="capitalize">{user.role}</TableCell>
            <TableCell>{format(new Date(user.createdAt), 'PPP')}</TableCell>
            <TableCell className="text-right flex justify-end gap-2">
                <Button
                    size="sm"
                    variant="outline"
                    disabled={isApproving || isRejecting}
                    onClick={() => reject({ userId: user.id })}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                    {isRejecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                </Button>
                <Button
                    size="sm"
                    variant="default"
                    disabled={isApproving || isRejecting}
                    onClick={() => approve({ userId: user.id })}
                    className="bg-green-600 hover:bg-green-700 text-white"
                >
                    {isApproving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                    Approve
                </Button>
            </TableCell>
        </TableRow>
    )
}

// --- Tab Components ---

const VehicleVerifications = () => {
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearchQuery = useDebounce(searchQuery, 500);

    const { data, isLoading, isError, error, refetch } = trpc.admin.getAllVehicles.useQuery({
        page,
        pageSize: 100, // Fetch more to enable meaningful grouping on frontend
        search: debouncedSearchQuery,
        verifiedStatus: false,
        orderBy: 'createdAt',
        orderDirection: 'asc',
    }, {
        refetchInterval: 5000 // Poll every 5 seconds
    });

    // Grouping Logic
    const groupedItems = React.useMemo(() => {
        if (!data?.vehicles) return [];

        const batches: Record<string, typeof data.vehicles> = {};
        const singles: typeof data.vehicles = [];

        data.vehicles.forEach(v => {
            const batchId = (v as any).bulkBatchId;
            if (batchId) {
                if (!batches[batchId]) batches[batchId] = [];
                batches[batchId].push(v);
            } else {
                singles.push(v);
            }
        });

        // Convert batches to single rows
        const batchRows = Object.values(batches).map(group => ({
            ...group[0],
            isBatch: true,
            batchCount: group.length,
            batchId: (group[0] as any).bulkBatchId,
            model: `${group[0].model.split('#')[0].trim()} (Batch x${group.length})`
        }));

        return [...batchRows, ...singles];
    }, [data?.vehicles]);

    const vehicles = groupedItems;
    const totalVehicles = groupedItems.length; // Approximate count logic for UI
    const totalPages = Math.ceil(totalVehicles / pageSize);

    // Batch Mutation
    const { mutate: verifyBatch, isLoading: isBatchLoading } = trpc.admin.verifyVehicleBatch.useMutation({
        onSuccess: (data) => {
            toast.success(`${data.count} vehicles verified successfully.`);
            refetch();
        },
        onError: (err) => toast.error(err.message)
    });

    return (
        <div className="space-y-4">
            <div className="flex w-full max-w-sm items-center space-x-2 px-4 sm:px-0">
                <Input
                    placeholder="Search by license plate or model..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-9 sm:h-10 text-sm"
                />
            </div>

            <div className="rounded-md border bg-card text-card-foreground shadow-sm overflow-x-auto mx-4 sm:mx-0">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-[150px]">Transporter</TableHead>
                            <TableHead className="w-[150px]">Vehicle</TableHead>
                            <TableHead className="w-[120px]">License Plate</TableHead>
                            <TableHead className="w-[120px]">Date Submitted</TableHead>
                            <TableHead className="text-right w-[100px]">Action</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading && (
                            Array.from({ length: 5 }).map((_, i) => (
                                <TableRow key={i}><TableCell colSpan={5} className="text-center p-4"><Skeleton className="h-8 w-full" /></TableCell></TableRow>
                            ))
                        )}
                        {isError && (
                            <TableRow><TableCell colSpan={5} className="text-center text-destructive p-8">Failed to load data: {error.message}</TableCell></TableRow>
                        )}
                        {!isLoading && vehicles.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} className="text-center p-12 text-muted-foreground">
                                    <div className="flex flex-col items-center gap-2">
                                        <Truck className="w-10 h-10" />
                                        <p className="font-semibold">No vehicles awaiting verification.</p>
                                    </div>
                                </TableCell>
                            </TableRow>
                        )}
                        {vehicles.map(v => {
                            if ((v as any).isBatch) {
                                return (
                                    <TableRow key={v.id}>
                                        <TableCell className="font-medium">{v.transporter?.profile?.fullName || 'N/A'}</TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <span className="font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded text-xs">BATCH</span>
                                                {v.model}
                                            </div>
                                        </TableCell>
                                        <TableCell className="font-mono text-muted-foreground italic">Multiple</TableCell>
                                        <TableCell>{format(new Date(v.createdAt), 'PPP')}</TableCell>
                                        <TableCell className="text-right">
                                            <Button
                                                size="sm"
                                                variant="default"
                                                disabled={isBatchLoading}
                                                onClick={() => verifyBatch({ batchId: (v as any).batchId })}
                                                className="bg-blue-600 hover:bg-blue-700 text-white"
                                            >
                                                {isBatchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                                                Verify All
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            }
                            return <VehicleVerificationRow key={v.id} vehicle={v} refetch={refetch} />;
                        })}
                    </TableBody>
                </Table>
            </div>

            <div className="flex justify-between items-center pt-4 px-4 sm:px-0">
                <p className="text-sm text-muted-foreground">
                    {/* Simplified pagination display for grouped view */}
                    Manage verifications
                </p>
            </div>
        </div>
    )
}

const CompanyVerifications = () => {
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearchQuery = useDebounce(searchQuery, 500);

    const { data, isLoading, isError, error, refetch } = trpc.admin.getAllUsers.useQuery({
        page,
        pageSize,
        search: debouncedSearchQuery,
        verificationStatus: VerificationStatus.PENDING,
        orderBy: 'createdAt',
        orderDirection: 'asc',
    }, {
        refetchInterval: 5000 // Poll every 5 seconds
    });

    const users = data?.users || [];
    const totalUsers = data?.totalCount || 0;
    const totalPages = Math.ceil(totalUsers / pageSize);

    return (
        <div className="space-y-4">
            <div className="flex w-full max-w-sm items-center space-x-2 px-4 sm:px-0">
                <Input
                    placeholder="Search by company or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-9 sm:h-10 text-sm"
                />
            </div>

            <div className="rounded-md border bg-card text-card-foreground shadow-sm overflow-x-auto mx-4 sm:mx-0">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-[150px]">Company Name</TableHead>
                            <TableHead className="w-[150px]">User</TableHead>
                            <TableHead className="w-[200px]">Email</TableHead>
                            <TableHead className="w-[100px]">Role</TableHead>
                            <TableHead className="w-[120px]">Date Submitted</TableHead>
                            <TableHead className="text-right w-[150px]">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading && (
                            Array.from({ length: 5 }).map((_, i) => (
                                <TableRow key={i}><TableCell colSpan={6} className="text-center p-4"><Skeleton className="h-8 w-full" /></TableCell></TableRow>
                            ))
                        )}
                        {isError && (
                            <TableRow><TableCell colSpan={6} className="text-center text-destructive p-8">Failed to load data: {error.message}</TableCell></TableRow>
                        )}
                        {!isLoading && users.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} className="text-center p-12 text-muted-foreground">
                                    <div className="flex flex-col items-center gap-2">
                                        <Building2 className="w-10 h-10" />
                                        <p className="font-semibold">No pending company verifications.</p>
                                    </div>
                                </TableCell>
                            </TableRow>
                        )}
                        {users.map(u => <CompanyVerificationRow key={u.id} user={u} refetch={refetch} />)}
                    </TableBody>
                </Table>
            </div>

            <div className="flex justify-between items-center pt-4 px-4 sm:px-0">
                <p className="text-sm text-muted-foreground">
                    Showing {Math.min(totalUsers, (page - 1) * pageSize + 1)} - {Math.min(totalUsers, page * pageSize)} of {totalUsers} companies
                </p>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(prev => Math.max(1, prev - 1))}
                        disabled={page === 1}
                    >
                        <ChevronLeft className="h-4 w-4" /> Previous
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(prev => prev + 1)}
                        disabled={page >= totalPages}
                    >
                        Next <ChevronRight className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </div>
    )
}

const VerificationsPage = () => {
    const { user } = useAuthStore();

    // Security Check
    if (user?.role !== 'ADMIN') {
        return (
            <div className="container mx-auto py-24 text-center">
                <Alert variant="destructive" className="max-w-md mx-auto">
                    <ShieldAlert className="h-4 w-4" />
                    <AlertTitle>Access Denied</AlertTitle>
                    <AlertDescription>You do not have permission to view this page.</AlertDescription>
                </Alert>
            </div>
        );
    }

    return (
        <div className="container mx-auto py-6 px-2 sm:px-4 animate-fade-in">
            <header className="space-y-1 sm:space-y-2 mb-4 sm:mb-8 px-2">
                <h1 className="text-2xl sm:text-4xl font-bold">Admin Panel</h1>
                <p className="text-muted-foreground text-sm sm:text-lg">Verification Queue</p>
            </header>

            <Card className="glass">
                <CardHeader className="p-4 sm:p-6 pb-2">
                    <CardTitle className="text-lg sm:text-2xl">Pending Approvals</CardTitle>
                    <CardDescription>Review and verify user documents and vehicle registrations.</CardDescription>
                </CardHeader>
                <CardContent className="p-0 sm:p-6 pt-2">
                    <Tabs defaultValue="vehicles" className="w-full">
                        <TabsList className="grid w-full grid-cols-2 mb-4">
                            <TabsTrigger value="vehicles" className="flex items-center gap-2">
                                <Truck className="w-4 h-4" /> Vehicles
                            </TabsTrigger>
                            <TabsTrigger value="companies" className="flex items-center gap-2">
                                <Building2 className="w-4 h-4" /> Companies
                            </TabsTrigger>
                        </TabsList>
                        <TabsContent value="vehicles">
                            <VehicleVerifications />
                        </TabsContent>
                        <TabsContent value="companies">
                            <CompanyVerifications />
                        </TabsContent>
                    </Tabs>
                </CardContent>
            </Card>
        </div>
    );
};

export default VerificationsPage;